import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { sportInfo } from "../lib/api";
import PageShell from "../components/PageShell";
import BottomNav from "../components/BottomNav";
import EmptyState from "../components/EmptyState";
import SportPills from "../components/SportPills";
import { ArrowLeft, Plus, Users as UsersIcon, MapPin, Lock } from "lucide-react";

export default function Teams() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("discover"); // discover | mine
  const [sport, setSport] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mine: tab === "mine" };
      if (sport) params.sport = sport;
      const { data } = await api.get("/teams", { params });
      setTeams(data);
    } catch (err) {
      console.warn("[teams] load failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [tab, sport]);

  useEffect(() => { load(); }, [load]);

  return (
    <PageShell>
      <header className="px-4 pt-6 pb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(-1)} data-testid="back-btn" className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Teams</div>
            <h1 className="font-heading text-2xl font-black tracking-tight">Crews & Teams</h1>
          </div>
        </div>
        <button
          data-testid="create-team-btn"
          onClick={() => navigate("/teams/new")}
          className="h-10 w-10 rounded-full bg-primary text-primary-foreground glow-cyan flex items-center justify-center shrink-0"
          aria-label="Buat team"
        >
          <Plus className="h-5 w-5" />
        </button>
      </header>

      {/* Tabs */}
      <div className="mt-4 px-4 flex gap-2">
        <TabBtn id="discover" current={tab} onClick={setTab} label="Discover" />
        <TabBtn id="mine" current={tab} onClick={setTab} label="My Teams" />
      </div>

      {/* Sport filter */}
      <div className="mt-4 px-4">
        <SportPills value={sport} onChange={setSport} />
      </div>

      {/* List */}
      <div className="mt-4 px-4 space-y-3">
        {loading && (
          <>
            <div className="h-24 bg-card rounded-2xl border border-border animate-pulse" />
            <div className="h-24 bg-card rounded-2xl border border-border animate-pulse" />
          </>
        )}
        {!loading && teams.length === 0 && (
          <EmptyState
            icon={tab === "mine" ? "👥" : "🔎"}
            title={tab === "mine" ? "Belum gabung team" : "Belum ada team"}
            body={tab === "mine" ? "Browse di tab Discover atau buat sendiri" : "Jadilah yang pertama bikin team di kota lo"}
            action={
              <button
                onClick={() => navigate("/teams/new")}
                className="mt-5 px-6 py-3 rounded-full bg-primary text-primary-foreground font-bold glow-cyan"
              >
                Buat Team
              </button>
            }
          />
        )}
        {teams.map((t) => <TeamCard key={t.team_id} team={t} />)}
      </div>

      <BottomNav />
    </PageShell>
  );
}

function TabBtn({ id, current, onClick, label }) {
  const active = id === current;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={`team-tab-${id}`}
      className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold transition-all ${active ? "bg-primary text-primary-foreground glow-cyan" : "bg-secondary border border-border text-muted-foreground"}`}
    >
      {label}
    </button>
  );
}

function TeamCard({ team }) {
  const s = sportInfo(team.sport);
  return (
    <Link
      to={`/teams/${team.team_id}`}
      data-testid={`team-card-${team.team_id}`}
      className="block bg-card border border-border rounded-2xl p-4 hover:border-primary/40 transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="h-14 w-14 rounded-2xl overflow-hidden flex items-center justify-center shrink-0" style={{ background: s.color + "22", border: `1px solid ${s.color}44` }}>
          {team.logo ? <img src={team.logo} alt={team.name} className="h-full w-full object-cover" /> : <span className="text-2xl">{s.icon}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="font-heading font-bold text-base truncate">{team.name}</div>
            {team.visibility === "private" && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {team.city}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><UsersIcon className="h-3 w-3" /> {(team.member_ids?.length || 0)} member</span>
          </div>
          {team.description && (
            <div className="mt-1.5 text-xs text-muted-foreground/80 line-clamp-1">{team.description}</div>
          )}
        </div>
      </div>
    </Link>
  );
}
